import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { catalogApi } from "@/lib/apiClient";
import { formatMoney, taxNote } from "@/lib/format";
import { PDP } from "@/constants/testIds";
import StoreHeader from "@/components/StoreHeader";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useCustomerAuth();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState("");
  const [mainIdx, setMainIdx] = useState(0);
  const [ordering, setOrdering] = useState(false);
  const [contact, setContact] = useState(null);

  useEffect(() => {
    let alive = true;
    setProduct(null);
    setError("");
    setMainIdx(0);
    catalogApi
      .product(slug)
      .then((p) => {
        if (alive) setProduct(p);
      })
      .catch((e) => {
        if (alive) {
          setError(
            e.response?.status === 404
              ? "This product is no longer available."
              : e.response?.data?.error?.message || "Failed to load product.",
          );
        }
      });
    // Pull the current WhatsApp/salesman contact for the Inquiry CTA.
    axios.get(`${BACKEND}/api/catalog/contact`).then((r) => alive && setContact(r.data)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader />
        <div className="max-w-3xl mx-auto px-6 py-16 text-center space-y-4">
          <h1 className="text-2xl font-semibold">Product unavailable</h1>
          <p className="text-muted-foreground">{error}</p>
          <Button asChild>
            <Link to="/catalog">Back to catalogue</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader />
        <div className="max-w-6xl mx-auto px-6 py-16 text-sm text-muted-foreground">
          Loading…
        </div>
      </div>
    );
  }

  const images = product.images && product.images.length > 0
    ? product.images
    : product.main_image
      ? [{ url: product.main_image, is_main: true }]
      : [];
  const mainImg = images[mainIdx] || images[0];
  const specs = Object.entries(product.specifications || {});

  return (
    <div className="min-h-screen bg-background" data-testid={PDP.root}>
      <PageHeader />
      <div className="max-w-6xl mx-auto px-6 py-8">
        <nav className="text-xs text-muted-foreground mb-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => nav(-1)}
            className="hover:text-foreground underline underline-offset-2"
            data-testid="pdp-back-btn"
          >
            ← Back
          </button>
          <span className="text-muted-foreground/50">|</span>
          <Link to="/" className="hover:text-foreground">Home</Link>
          <span className="mx-1">/</span>
          <Link to="/catalog" className="hover:text-foreground">Catalogue</Link>
          <span className="mx-1">/</span>
          <span className="text-foreground truncate">{product.name}</span>
        </nav>

        <div className="grid gap-10 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          {/* Gallery */}
          <div>
            <div className="aspect-square bg-muted rounded-lg overflow-hidden border">
              {mainImg && (
                <img
                  src={mainImg.url}
                  alt={product.name}
                  data-testid={PDP.mainImage}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto" data-testid={PDP.thumbList}>
                {images.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setMainIdx(i)}
                    className={`w-20 h-20 flex-none rounded border overflow-hidden ${i === mainIdx ? "ring-2 ring-black" : ""}`}
                  >
                    <img src={img.thumb_url || img.url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="space-y-6">
            <div>
              {product.brand && (
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2" data-testid={PDP.brand}>
                  {product.brand}
                </div>
              )}
              <h1 className="text-2xl sm:text-3xl font-semibold" data-testid={PDP.name}>
                {product.name}
              </h1>
              <div className="text-xs text-muted-foreground mt-2" data-testid={PDP.sku}>
                Part No: {product.sku}
              </div>
              {!product.in_stock && (
                <div
                  className="mt-3 inline-flex items-center gap-2 border border-red-500 bg-red-50 text-red-700 rounded px-3 py-1.5 text-sm font-semibold"
                  data-testid="pdp-unavailable-banner"
                >
                  <span className="inline-flex w-5 h-5 rounded-full bg-red-600 text-white items-center justify-center text-xs font-bold">!</span>
                  Not available
                </div>
              )}
            </div>

            <div>
              <div className="flex items-baseline gap-3">
                <div className="text-3xl font-semibold" data-testid={PDP.price}>
                  {formatMoney(product.price)}
                </div>
                {product.compare_at_price && (
                  <div className="text-base text-muted-foreground line-through">
                    {formatMoney(product.compare_at_price)}
                  </div>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-1" data-testid={PDP.taxNote}>
                {taxNote(product.tax_inclusive, product.vat_rate)}
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {product.in_stock ? (
                <Badge variant="secondary" data-testid={PDP.stockPill}>
                  Available
                </Badge>
              ) : (
                <Badge variant="destructive" data-testid={PDP.stockPill}>Not available</Badge>
              )}
              <Badge
                variant={product.product_type === "genuine" ? "default" : "outline"}
                className={product.product_type === "genuine" ? "bg-emerald-600 hover:bg-emerald-600" : ""}
                data-testid="pdp-product-type"
              >
                {product.product_type === "genuine" ? "Genuine / OEM" : "Aftermarket"}
              </Badge>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <EnquireModalButton product={product} contact={contact} user={user} />
            </div>

            {product.description && (
              <>
                <Separator />
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                    Description
                  </div>
                  <p className="text-sm leading-relaxed" data-testid={PDP.description}>
                    {product.description}
                  </p>
                </div>
              </>
            )}

            {specs.length > 0 && (
              <>
                <Separator />
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                    Specifications
                  </div>
                  <table className="w-full text-sm border rounded overflow-hidden" data-testid={PDP.specsTable}>
                    <tbody>
                      {specs.map(([k, v]) => (
                        <tr key={k} className="border-b last:border-0">
                          <td className="py-2 px-3 text-muted-foreground w-1/3">{k}</td>
                          <td className="py-2 px-3">{String(v)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PageHeader() {
  return <StoreHeader />;
}

/**
 * Enquire / Negotiate modal — the ONLY customer purchase path. Captures
 * name/phone/email/message, POSTs to /api/catalog/inquiries, and then shows
 * a success card. Signed-in customer details prefill the form.
 */
function EnquireModalButton({ product, contact, user }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    name: user?.full_name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    note: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      setErr("Please share your name and phone number so our team can reach you.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const { inquiriesApi } = await import("@/lib/commerce");
      const res = await inquiriesApi.log({
        product_id: product.id,
        customer_name: form.name.trim(),
        customer_email: form.email.trim() || undefined,
        customer_phone: form.phone.trim(),
        note: form.note.trim() || undefined,
        page_url: window.location.href,
      });
      setSent(res || { status: "ok" });
    } catch (ex) {
      setErr(ex.response?.data?.error?.message || "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button
        size="lg"
        className="flex-1 bg-red-600 hover:bg-red-500 text-white"
        onClick={() => { setOpen(true); setSent(null); setErr(""); }}
        disabled={!product.in_stock}
        data-testid="pdp-enquire-btn"
      >
        {product.in_stock ? "Enquire / Negotiate" : "Not available"}
      </Button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="pdp-enquire-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
        {sent ? (
          <>
            <h3 className="text-xl font-semibold text-green-700">Enquiry sent</h3>
            <p className="text-sm text-slate-700">
              Your enquiry has been received successfully. Our team will review your enquiry and contact you shortly.
            </p>
            <p className="text-xs text-muted-foreground">
              You&rsquo;ll receive a confirmation email at <b>{form.email || "your address"}</b>.
              {user && <> You can also see status updates under <a className="underline" href="/my/enquiries">My enquiries</a>.</>}
            </p>
            <Button className="w-full" onClick={() => setOpen(false)} data-testid="pdp-enquire-close">Done</Button>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="pdp-enquire-form">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-xl font-semibold">Enquire about this part</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {product.name}
                </p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600 text-2xl leading-none" aria-label="Close">&times;</button>
            </div>
            {err && <div className="text-sm text-red-600">{err}</div>}
            <div className="space-y-2">
              <label className="text-xs font-medium">Your name <span className="text-red-500">*</span></label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" data-testid="pdp-enquire-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-xs font-medium">Phone <span className="text-red-500">*</span></label>
                <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+971…" className="w-full border rounded px-3 py-2 text-sm" data-testid="pdp-enquire-phone" />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" data-testid="pdp-enquire-email" />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium">Message (optional)</label>
              <textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Quantity, colour, vehicle year, price expectations…" className="w-full border rounded px-3 py-2 text-sm resize-none" data-testid="pdp-enquire-note" />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-red-600 hover:bg-red-500 text-white" data-testid="pdp-enquire-submit">
              {busy ? "Sending…" : "Send enquiry"}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">
              Our salesman will contact you privately. Your details are never shared publicly.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

/**
 * WhatsApp inquiry / negotiate button. The salesman's WhatsApp number is
 * fetched at runtime from /api/catalog/contact, so admins can rotate it
 * from Settings without any code change. Falls back gracefully if the
 * number hasn't been configured yet.
 */
function InquiryButton({ product, contact, user }) {
  // Uniformly pick a salesman from the roster on every PDP mount — every
  // product visit gets an equal chance of routing to any active salesman.
  // Falls back to the legacy `whatsapp` field when no roster is configured.
  const picked = useMemo(() => {
    const roster = (contact?.salesmen || []).filter((s) => (s?.whatsapp || "").trim());
    if (roster.length === 0) {
      return {
        whatsapp: contact?.whatsapp || "",
        name: contact?.salesman_name || "",
      };
    }
    const s = roster[Math.floor(Math.random() * roster.length)];
    return { whatsapp: s.whatsapp, name: s.name || "" };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id, contact?.salesmen?.length, contact?.whatsapp]);

  const raw = (picked.whatsapp || "").replace(/[^\d]/g, "");
  const disabled = !raw;

  const buildHref = () => {
    if (!raw) return "#";
    const parts = [];
    parts.push(
      contact?.inquiry_default_message?.trim() ||
        `Hello${picked.name ? ` ${picked.name}` : ""}, I would like to inquire about this product.`,
    );
    parts.push("");
    parts.push(`Product: ${product.name}`);
    parts.push(`SKU: ${product.sku}`);
    if (product.brand) parts.push(`Brand: ${product.brand}`);
    parts.push(`Link: ${window.location.href}`);
    if (user?.full_name) parts.push(`\nFrom: ${user.full_name}`);
    const msg = encodeURIComponent(parts.join("\n"));
    return `https://wa.me/${raw}?text=${msg}`;
  };

  const onClick = () => {
    // Best-effort log — never blocks the WhatsApp deeplink.
    import("@/lib/commerce").then(({ inquiriesApi }) => {
      inquiriesApi.log({
        product_id: product.id,
        customer_name: user?.full_name || undefined,
        customer_email: user?.email || undefined,
        customer_phone: user?.phone || undefined,
        page_url: window.location.href,
        note: picked.name ? `Routed to ${picked.name}` : undefined,
      });
    });
  };

  return (
    <Button
      asChild={!disabled}
      size="lg"
      variant="outline"
      className="flex-1 border-green-600 text-green-700 hover:bg-green-50 hover:text-green-800"
      disabled={disabled}
      title={disabled ? "Inquiry contact not configured yet" : undefined}
      data-testid="pdp-inquiry-btn"
    >
      {disabled ? (
        <span>Inquiry unavailable</span>
      ) : (
        <a href={buildHref()} target="_blank" rel="noreferrer" onClick={onClick}>
          💬 Inquiry / Negotiate
        </a>
      )}
    </Button>
  );
}
